import { AccountancyValidationStatus } from '../accountancy/accountancy-validation-status';
import { DataExchangeKind, JpkVatSubkind, PackageOfDocumentsSubkind } from '../accountancy/data-exchange-kind';
import { DataExchangePeriodType } from '../accountancy/data-exchange-period-type';
import { DataExchangeStatus } from '../accountancy/data-exchange-status';
import { DdkState } from '../accountancy/ddk-state';
import { JpkVatState } from '../accountancy/jpk-vat-state';
import { PurchaseDocumentsDateSelectionKind } from '../accountancy/purchase-date-selection-kind';
import { SalesDocumentsDateSelectionKind } from '../accountancy/sales-date-selection-kind';
import { SentDocumentsFilteringPolicy } from '../accountancy/sent-documents-filtering-policy';
import { AnnouncementContentKind } from '../announcement/announcement-content-kind';
import { BackgroundTaskKind } from '../announcement/background-task-kind';
import { FitContent } from '../background/fit-content';
import { BankAccountStatus } from '../bank-account/bank-account-status';
import { ClientKind } from '../client/client-kind';
import { ClientOtherBuyerRole } from '../client/client-other-buyer-role';
import { TinKind } from '../client/tax-identification-numer-kind';
import { CalculationMethod } from '../common/calculation-method';
import { EInvoiceState } from '../common/e-invoice-state';
import { ExportKind } from '../common/export-kind';
import { FileMode } from '../common/file-mode';
import { GapFillingMethod } from '../common/gap-filling-method';
import { RealizeState } from '../common/realize-state';
import { FormOfSettlementWithTaxOffice } from '../company/form-of-settlement-with-tax-office';
import { PersonKind } from '../company/person-kind';
import { RoleRelativeToOtherSeller } from '../company/role-relative-to-other-seller';
import { TaxReturnFrequency } from '../company/tax-return-frequency';
import { VatReleaseReasonSource } from '../company/vat-release-reason-source';
import { ZusContribution } from '../company/zus-contribution';
import { DocumentVatIssueMode } from '../document/document-vat-issue-mode';
import { EInvoiceCreationValidationCode, EInvoiceCreationValidationErrorCode } from '../e-invoice/e-invoice-creation-validation';
import { AggregatedEmailStatusCode } from '../email/aggregated-email-status-code';
import { EmailDeliveryStatusCode } from '../email/email-delivery-status-code';
import { EmailMultiMailSourceType } from '../email/email-multi-mail-source-type';
import { FiscalDeviceEventStatus } from '../fiscal/fiscal-device-event';
import { FiscalizationState } from '../fiscal/fiscal-state';
import { ImportDuplicates } from '../import/import-duplicates';
import { ImportClientsIdentification, ImportProductsIdentification } from '../import/import-identification';
import { ImportedItemStatus } from '../import/import-status';
import { InventoryCostCalculationMethod } from '../inventory/inventory-cost-calculation-method';
import { InventoryDocumentKind } from '../inventory/inventory-document-kind';
import { InventoryEffect } from '../inventory/inventory-effect';
import { InventoryLevelKind } from '../inventory/inventory-level-kind';
import { InventoryRelationshipCreationMethod } from '../inventory/inventory-relationship-creation-method';
import { KsefCollectiveIdentifierOperationErrorCode } from '../ksef/ksef-collective-identifier-operation-error-codes';
import { KsefCredentialsRoleType } from '../ksef/ksef-credentials-role-type';
import { KsefDocumentSendingError } from '../ksef/ksef-document-sending-error';
import { KsefDocumentStatus } from '../ksef/ksef-document-status';
import { KsefReceiveSubmissionMode } from '../ksef/ksef-receive-submission-mode';
import { KsefReceivedDocumentKind } from '../ksef/ksef-received-document-kind';
import { KsefReceivedDocumentStatus } from '../ksef/ksef-received-document-status';
import { KsefReceivingSubjectRole } from '../ksef/ksef-receiving-subject-role';
import { KsefSendParametersSalePatternSendMode } from '../ksef/ksef-send-parameters-sale-pattern-send-mode';
import { KsefSendSubmissionMode } from '../ksef/ksef-send-submission-mode';
import { ImportKsefXmlErrorCode } from '../ksef/ksef-xml-import-error-code';
import { SaleSendToKsefValidationError } from '../ksef/sale-send-to-ksef-validation-error';
import { SaleSendToKsefValidationWarning } from '../ksef/sale-send-to-ksef-validation-warning';
import { ModuleName } from '../license/module-name';
import { MailTemplateAutotext } from '../mail-template/mail-template-autotext';
import { MailTemplateKind } from '../mail-template/mail-template-kind';
import { NumberingFormatAutotext } from '../numbering/numbering-format-autotext';
import { OrderDocumentKind } from '../orders/common/order-document-kind';
import { PaymentStatus } from '../payment/payment-status';
import { PrintingPatternLanguage } from '../printing-pattern/printing-pattern-language';
import { ProductKind } from '../product/product-kind';
import { PurchaseDocumentKind } from '../purchase/purchase-document-kind';
import { AdvanceState } from '../sale/advance-state';
import { RateNP } from '../sale/rate-np';
import { SaleDocumentKind } from '../sale/sale-document-kind';
import { OrderState } from '../sale/sale-order-state';
import { SalePatternAutotext } from '../sale/sale-pattern-autotexts';
import { SalePatternIssueBusinessDayMode } from '../sale/sale-pattern-issue-business-day-mode';
import { SalePatternKsefSendOption } from '../sale/sale-pattern-ksef-send-option';
import { SalePatternSchedule } from '../sale/sale-pattern-schedule';

export type TEnum = unknown;

export const enumMap = new Map<TEnum, string>();

enumMap.set(ProductKind, 'ProductKind');
enumMap.set(BackgroundTaskKind, 'BackgroundTaskKind');
enumMap.set(ImportedItemStatus, 'ImportedItemStatus');
enumMap.set(ImportClientsIdentification, 'ImportClientsIdentification');
enumMap.set(ImportDuplicates, 'ImportDuplicates');
enumMap.set(ImportProductsIdentification, 'ImportProductsIdentification');
enumMap.set(ClientKind, 'ClientKind');
enumMap.set(TinKind, 'TinKind');
enumMap.set(SaleDocumentKind, 'SaleDocumentKind');
enumMap.set(PurchaseDocumentKind, 'PurchaseDocumentKind');
enumMap.set(MailTemplateKind, 'MailTemplateKind');
enumMap.set(MailTemplateAutotext, 'MailTemplateAutotext');
enumMap.set(CalculationMethod, 'CalculationMethod');
enumMap.set(FormOfSettlementWithTaxOffice, 'FormOfSettlementWithTaxOffice');
enumMap.set(ZusContribution, 'ZusContribution');
enumMap.set(PersonKind, 'PersonKind');
enumMap.set(TaxReturnFrequency, 'TaxReturnFrequency');
enumMap.set(ModuleName, 'ModuleName');
enumMap.set(GapFillingMethod, 'GapFillingMethod');
enumMap.set(NumberingFormatAutotext, 'NumberingFormatAutotext');
enumMap.set(AnnouncementContentKind, 'AnnouncementContentKind');
enumMap.set(DataExchangeStatus, 'DataExchangeStatus');
enumMap.set(DataExchangeKind, 'DataExchangeKind');
enumMap.set(JpkVatSubkind, 'JpkVatSubkind');
enumMap.set(PackageOfDocumentsSubkind, 'PackageOfDocumentsSubkind');
enumMap.set(DataExchangePeriodType, 'DataExchangePeriodType');
enumMap.set(SalesDocumentsDateSelectionKind, 'SalesDocumentsDateSelectionKind');
enumMap.set(PurchaseDocumentsDateSelectionKind, 'PurchaseDocumentsDateSelectionKind');
enumMap.set(SentDocumentsFilteringPolicy, 'SentDocumentsFilteringPolicy');
enumMap.set(ExportKind, 'ExportKind');
enumMap.set(PaymentStatus, 'PaymentStatus');
enumMap.set(BankAccountStatus, 'BankAccountStatus');
enumMap.set(SalePatternAutotext, 'SalePatternItemAutotext');
enumMap.set(SalePatternSchedule, 'SalePatternSchedule');
enumMap.set(AggregatedEmailStatusCode, 'AggregatedEmailStatusCode');
enumMap.set(AdvanceState, 'AdvanceState');
enumMap.set(RateNP, 'RateNP');
enumMap.set(RealizeState, 'RealizeState');
enumMap.set(FiscalizationState, 'FiscalizationState');
enumMap.set(OrderState, 'OrderState');
enumMap.set(EmailDeliveryStatusCode, 'EmailDeliveryStatusCode');
enumMap.set(FileMode, 'FileMode');
enumMap.set(FitContent, 'FitContent');
enumMap.set(AccountancyValidationStatus, 'AccountancyValidations');
enumMap.set(SalePatternIssueBusinessDayMode, 'SalePatternIssueBusinessDayMode');
enumMap.set(EInvoiceState, 'EInvoiceState');
enumMap.set(InventoryLevelKind, 'InventoryLevelKind');
enumMap.set(InventoryDocumentKind, 'InventoryDocumentKind');
enumMap.set(InventoryRelationshipCreationMethod, 'InventoryRelationshipCreationMethod');
enumMap.set(KsefCredentialsRoleType, 'KsefCredentialsRoleType');
enumMap.set(SaleSendToKsefValidationError, 'SaleSendToKsefValidationError');
enumMap.set(SaleSendToKsefValidationWarning, 'SaleSendToKsefValidationWarning');
enumMap.set(JpkVatState, 'JpkVatState');
enumMap.set(DdkState, 'DdkState');
enumMap.set(KsefDocumentStatus, 'KsefDocumentStatus');
enumMap.set(InventoryCostCalculationMethod, 'InventoryCostCalculationMethod');
enumMap.set(VatReleaseReasonSource, 'VatReleaseReasonSource');
enumMap.set(EmailMultiMailSourceType, 'EmailMultiMailSourceType');
enumMap.set(InventoryEffect, 'InventoryEffect');
enumMap.set(PrintingPatternLanguage, 'PrintingPatternLanguage');
enumMap.set(KsefDocumentSendingError, "KsefDocumentSendingError");
enumMap.set(KsefReceivedDocumentKind, "KsefReceivedDocumentKind");
enumMap.set(KsefReceivedDocumentStatus, "KsefReceivedDocumentStatus");
enumMap.set(EInvoiceCreationValidationErrorCode, "EInvoiceCreationValidationErrorCode");
enumMap.set(EInvoiceCreationValidationCode, "EInvoiceCreationValidationCode");
enumMap.set(RoleRelativeToOtherSeller, "RoleRelativeToOtherSeller");
enumMap.set(FiscalDeviceEventStatus, "FiscalDeviceEventStatus");
enumMap.set(OrderDocumentKind, "OrderDocumentKind");
enumMap.set(ClientOtherBuyerRole, 'ClientOtherBuyerRole');
enumMap.set(KsefReceiveSubmissionMode, 'KsefReceiveSubmissionMode');
enumMap.set(KsefReceivingSubjectRole, 'KsefReceivingSubjectRole');
enumMap.set(KsefSendSubmissionMode, 'KsefSendSubmissionMode');
enumMap.set(KsefSendParametersSalePatternSendMode, 'KsefSendParametersSalePatternSendMode');
enumMap.set(SalePatternKsefSendOption, 'SalePatternKsefSendOption');
enumMap.set(DocumentVatIssueMode, 'DocumentVatIssueMode');
enumMap.set(ImportKsefXmlErrorCode, 'ImportKsefXmlErrorCode');
enumMap.set(KsefCollectiveIdentifierOperationErrorCode, 'KsefCollectiveIdentifierOperationErrorCode');